import { useAuth } from "@/core/components/auth/store/auth";
import { Button } from "@/core/components/ui/button";
import { Checkbox } from "@/core/components/ui/checkbox";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { cn } from "@/core/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft, Save } from "lucide-react";
import { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { REPORT_TOOLS } from "../../constants/report-tools";
import { fromApiFrequency } from "../../mappers/frequency";
import { isBrazilianUser } from "../../mappers/locale";
import { findPhoneRecipient, fromApiPhone } from "../../mappers/phone";
import {
  CHANNELS,
  FREQUENCIES,
  scheduleFormSchema,
  ScheduleFormValues,
  WEEKDAYS,
} from "../../schema/schedule-form-schema";
import { NotificationSchedule } from "../../types";
import { useScheduleTracking } from "../../tracking";
import { PhoneField } from "../phone-field";
import { TimePicker } from "../time-picker";

const TIMEZONES = [
  { value: "America/Sao_Paulo", labelKey: "schedule-timezone-brasilia" },
];

interface ScheduleFormProps {
  schedule?: NotificationSchedule;
  isSubmitting?: boolean;
  onCancel: () => void;
  onSubmit: (values: ScheduleFormValues) => void;
}

export function ScheduleForm({
  schedule,
  isSubmitting,
  onCancel,
  onSubmit,
}: ScheduleFormProps) {
  const [translate] = useTranslation(["insights"]);
  const userPhone = useAuth((state) => state.userInfo?.profile?.phone);
  // O relatório vai para o e-mail do usuário logado — o backend resolve o
  // destinatário pelo JWT, então aqui o campo é apenas informativo.
  const userEmail = useAuth((state) => state.userInfo?.profile?.email);
  // `user.country` primeiro; `plan.country` como reserva (MeProfile não tem
  // country — só MeUser e MePlan têm).
  const userCountry = useAuth(
    (state) => state.userInfo?.user?.country ?? state.userInfo?.plan?.country,
  );
  const userLocale = useAuth((state) => state.userInfo?.user?.locale);

  // WhatsApp depende de DDI/máscara brasileiros.
  const canUseWhatsApp = isBrazilianUser(userCountry, userLocale);

  // INTERVAL/CRON da API de volta para frequência + dias da UI.
  const apiFrequency = schedule ? fromApiFrequency(schedule) : null;

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
  } = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleFormSchema),
    defaultValues: {
      toolKey: schedule?.tool_key ?? REPORT_TOOLS[0].toolKey,
      frequency: apiFrequency?.frequency ?? "weekly",
      // Só um dia na UI. Agendamentos salvos antes dessa regra podem ter mais
      // de um no cron (`MON,WED`) — fica o primeiro, para não exibir uma
      // seleção que o usuário não consegue reproduzir.
      weekdays: apiFrequency?.weekdays.slice(0, 1) ?? [],
      time: schedule?.send_time ?? "09:00",
      timezone: schedule?.timezone ?? "America/Sao_Paulo",
      // `email` é sempre incluído: é obrigatório e não desmarcável na UI.
      // `whatsapp` só entra se o usuário puder usá-lo — do contrário o canal
      // ficaria marcado sem checkbox visível nem campo de telefone.
      channels: Array.from(
        new Set<(typeof CHANNELS)[number]>([
          "email",
          ...(schedule?.channels ?? []).filter(
            (channel): channel is (typeof CHANNELS)[number] =>
              channel === "whatsapp" &&
              isBrazilianUser(userCountry, userLocale),
          ),
        ]),
      ),
      // `recipients` mistura e-mails e telefone, e o backend põe o e-mail do
      // usuário logado na frente — busca pelo formato em vez de índice fixo.
      phone: fromApiPhone(
        findPhoneRecipient(schedule?.recipients) ?? userPhone ?? "",
      ),
    },
  });

  const tracking = useScheduleTracking();
  // `schedule` presente = edição; o plano pede `mode` em report/frequency/save.
  const mode = schedule ? "edit" : "new";

  const frequency = watch("frequency");
  const channels = watch("channels");
  const showWeekdays = frequency === "weekly";
  const showWhatsApp = canUseWhatsApp && channels?.includes("whatsapp");

  // `has_saved_number` = já vinha número do perfil/agendamento, antes de o usuário digitar.
  const hadSavedPhone = useRef(Boolean(getValues("phone"))).current;

  // Evento de "Tela": dispara na primeira vez que a confirmação do WhatsApp aparece,
  // não a cada re-render (o usuário pode marcar/desmarcar o canal várias vezes).
  const whatsappViewTracked = useRef(false);
  useEffect(() => {
    if (showWhatsApp && !whatsappViewTracked.current) {
      whatsappViewTracked.current = true;
      tracking.whatsappConfirmViewed(hadSavedPhone);
    }
  }, [showWhatsApp, hadSavedPhone, tracking]);

  const visibleChannels = canUseWhatsApp
    ? CHANNELS
    : CHANNELS.filter((channel) => channel !== "whatsapp");

  const toggleInArray = <T,>(list: T[], item: T): T[] =>
    list.includes(item) ? list.filter((i) => i !== item) : [...list, item];

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-6 pb-4"
    >
      <button
        type="button"
        onClick={onCancel}
        className="flex gap-1 items-center self-start text-sm text-neutral-500 hover:text-neutral-900"
      >
        <ChevronLeft className="w-4 h-4" />
        {translate("schedule-title")}
      </button>

      <h2 className="text-2xl font-bold">
        {schedule
          ? translate("schedule-form-edit-title")
          : translate("schedule-form-new-title")}
      </h2>

      <div className="flex flex-col gap-6 p-6 bg-white rounded-xl border border-secondary-200">
        {/* Relatório */}
        <section className="flex flex-col gap-3">
          <div>
            <h3 className="text-lg font-bold">
              {translate("schedule-section-report")}
            </h3>
            <p className="text-sm text-neutral-500">
              {translate("schedule-section-report-hint")}
            </p>
          </div>
          <Controller
            control={control}
            name="toolKey"
            render={({ field }) => (
              <div className="flex flex-col gap-2">
                {REPORT_TOOLS.map((tool) => {
                  const selected = field.value === tool.toolKey;
                  return (
                    <button
                      type="button"
                      key={tool.toolKey}
                      onClick={() => {
                        tracking.reportSelected(tool.toolKey, mode);
                        field.onChange(tool.toolKey);
                      }}
                      className={cn(
                        "flex gap-3 items-center p-4 text-left rounded-lg border transition-colors",
                        selected
                          ? "border-neutral-900"
                          : "border-secondary-200 hover:border-secondary-300",
                      )}
                    >
                      <span
                        className={cn(
                          "flex justify-center items-center w-4 h-4 rounded-full border shrink-0",
                          selected
                            ? "border-neutral-900"
                            : "border-neutral-400",
                        )}
                      >
                        {selected && (
                          <span className="w-2 h-2 rounded-full bg-neutral-900" />
                        )}
                      </span>
                      <span className="flex flex-col">
                        <span className="text-sm font-bold">
                          {translate(`${tool.labelKey}-title`)}
                        </span>
                        <span className="text-sm text-neutral-500">
                          {translate(`${tool.labelKey}-description`)}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          />
        </section>

        {/* Frequência */}
        <section className="flex flex-col gap-3">
          <div>
            <h3 className="text-lg font-bold">
              {translate("schedule-section-frequency")}
            </h3>
            <p className="text-sm text-neutral-500">
              {translate("schedule-section-frequency-hint")}
            </p>
          </div>
          <Controller
            control={control}
            name="frequency"
            render={({ field }) => (
              <div className="flex flex-wrap gap-2">
                {FREQUENCIES.map((freq) => {
                  const selected = field.value === freq;
                  return (
                    <button
                      type="button"
                      key={freq}
                      onClick={() => {
                        tracking.frequencySelected(
                          freq,
                          watch("toolKey"),
                          mode,
                        );
                        field.onChange(freq);
                      }}
                      className={cn(
                        "px-4 py-2 text-sm rounded-md border transition-colors",
                        selected
                          ? "text-white border-neutral-900 bg-neutral-900"
                          : "border-secondary-200 hover:border-secondary-300",
                      )}
                    >
                      {translate(`schedule-frequency-${freq}`)}
                    </button>
                  );
                })}
              </div>
            )}
          />
        </section>

        {/* Dia da semana */}
        {showWeekdays && (
          <section className="flex flex-col gap-3">
            <div>
              <h3 className="text-lg font-bold">
                {translate("schedule-section-weekday")}
              </h3>
              <p className="text-sm text-neutral-500">
                {translate("schedule-section-weekday-hint")}
              </p>
            </div>
            <Controller
              control={control}
              name="weekdays"
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {WEEKDAYS.map((day) => {
                    const selected = field.value.includes(day);
                    return (
                      <button
                        type="button"
                        key={day}
                        // Seleção única: o clique substitui o dia escolhido.
                        // Clicar no que já está marcado não desmarca — zero
                        // dias é inválido, e o form guarda um array só porque
                        // é o que o cron da API consome.
                        onClick={() => field.onChange([day])}
                        aria-pressed={selected}
                        className={cn(
                          "px-4 py-2 text-sm rounded-md border transition-colors",
                          selected
                            ? "text-white border-neutral-900 bg-neutral-900"
                            : "border-secondary-200 hover:border-secondary-300",
                        )}
                      >
                        {translate(`schedule-weekday-${day}`)}
                      </button>
                    );
                  })}
                </div>
              )}
            />
            {errors.weekdays && (
              <p className="text-xs text-red-500">
                {translate("schedule-error-weekdays-required")}
              </p>
            )}
          </section>
        )}

        {/* Horário */}
        <section className="flex flex-col gap-3">
          <h3 className="text-lg font-bold">
            {translate("schedule-section-time")}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label>{translate("schedule-field-hour")}</Label>
              <Controller
                control={control}
                name="time"
                render={({ field }) => (
                  <TimePicker
                    {...field}
                    aria-label={String(translate("schedule-field-hour"))}
                    hourLabel={String(translate("schedule-field-hour"))}
                    minuteLabel={String(translate("schedule-field-minute"))}
                  />
                )}
              />
              {errors.time && (
                <p className="text-xs text-red-500">
                  {translate("schedule-error-time-invalid")}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{translate("schedule-field-timezone")}</Label>
              <Controller
                control={control}
                name="timezone"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {translate(tz.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        </section>

        {/* Canais de envio */}
        <section className="flex flex-col gap-3">
          <div>
            <h3 className="text-lg font-bold">
              {translate("schedule-section-channels")}
            </h3>
            <p className="text-sm text-neutral-500">
              {translate("schedule-section-channels-hint")}
            </p>
          </div>
          <Controller
            control={control}
            name="channels"
            render={({ field }) => (
              <div className="flex flex-col gap-2">
                {visibleChannels.map((channel) => {
                  const checked = field.value.includes(channel);
                  // E-mail é obrigatório: sempre marcado e não desmarcável,
                  // garantindo que channels nunca chegue vazio na API.
                  const locked = channel === "email";
                  return (
                    <label
                      key={channel}
                      className={cn(
                        "flex gap-3 items-center p-4 text-sm rounded-lg border transition-colors",
                        locked ? "cursor-default" : "cursor-pointer",
                        checked
                          ? "border-neutral-900"
                          : "border-secondary-200 hover:border-secondary-300",
                      )}
                    >
                      <Checkbox
                        checked={locked ? true : checked}
                        disabled={locked}
                        onCheckedChange={() => {
                          if (locked) return;
                          // Só o "selecionou" é Must; `channel_unselected` é Should.
                          if (!checked) tracking.channelSelected(channel);
                          field.onChange(toggleInArray(field.value, channel));
                        }}
                      />
                      {translate(`schedule-channel-${channel}`)}
                      {/* O e-mail do destinatário é o do usuário logado. */}
                      {locked && userEmail && (
                        <span className="text-neutral-500">({userEmail})</span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          />
          {errors.channels && (
            <p className="text-xs text-red-500">
              {translate("schedule-error-channels-required")}
            </p>
          )}
        </section>

        {/* WhatsApp */}
        {showWhatsApp && (
          <section className="flex flex-col gap-3">
            <div>
              <h3 className="text-lg font-bold">
                {translate("schedule-section-whatsapp")}
              </h3>
              <p className="text-sm text-neutral-500">
                {translate("schedule-section-whatsapp-hint")}
              </p>
            </div>
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <PhoneField
                  value={field.value ?? ""}
                  onChange={(phone) => {
                    // O PhoneField só chama onChange ao confirmar "Salvar" — é a
                    // atualização de fato. Sem o número: só o evento.
                    if (phone !== field.value) {
                      tracking.whatsappNumberUpdated();
                    }
                    field.onChange(phone);
                  }}
                  errorMessage={
                    errors.phone
                      ? (translate("schedule-error-phone-invalid") as string)
                      : undefined
                  }
                />
              )}
            />
          </section>
        )}
      </div>

      <div className="flex gap-3 justify-between">
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="flex-1"
          onClick={onCancel}
        >
          {translate("schedule-form-cancel")}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="flex-1 gap-2"
          disabled={isSubmitting}
        >
          <Save className="w-4 h-4" />
          {translate("schedule-form-save")}
        </Button>
      </div>
    </form>
  );
}
